import React, { useEffect, Fragment, useState, useContext } from "react";
import { ThemeProvider } from "@mui/material/styles";
import { CssBaseline, Box, Typography, Alert, AlertTitle } from "@mui/material";
import { SnackbarProvider } from "notistack";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { AppRouter } from "./AppRouter";
import { AuthContext, AuthContextProvider } from "./context";
import { BrowserTitleContext, BrowserTitleContextProvider } from "./context/browserTitle";
import { ModalContext } from "./context/modalContext";
import { getStoredUserAuth } from "./utils/LocalStorage";
import { useAuthHandler } from "./custom-hooks/AuthHandler";
import withClearCache from "./ClearCache";
import { WsSocketContextProvider } from "./context/wsSocket";
import { ErrorContextProvider } from "./context/errorContext";
import useLogoutHandler from "./custom-hooks/logoutHandler";
import CustomModal from "./components/CustomModal";
import Button from "./components/Button";
import useRedirectHandler from "./custom-hooks/redirectHandler";
import Spinner from "./components/Spinner";
import ErrorBoundary from "./components/ErrorBoundary";
import theme from "./theme/theme";
import { info } from "./services/logging-service";

/**
 * Session Expiry Modal Component
 * Uses revokeTokenHandler from AuthContext to properly refresh tokens
 * Listens for global sessionExpired events from any part of the app
 */
const SessionExpiryModal = () => {
    const { revokeTokenHandler } = useContext(AuthContext);
    const { logout } = useLogoutHandler();
    const { isModalOpen, openModal } = useContext(ModalContext);

    useEffect(() => {
        console.log("✅ SessionExpiryModal mounted!");

        // Listen for global session expired events
        const handleSessionExpired = (event) => {
            const { type } = event.detail || {};
            console.log("📢 Global sessionExpired event received:", type);

            if (type === "forbidden") {
                openModal("unauthorizedAlert");
            } else {
                openModal("logoutAlert");
            }
        };

        window.addEventListener("sessionExpired", handleSessionExpired);

        return () => {
            window.removeEventListener("sessionExpired", handleSessionExpired);
        };
    }, [openModal]);

    useEffect(() => {
        const isOpen = isModalOpen("logoutAlert");
        console.log("📢 logoutAlert modal state changed:", isOpen);
    }, [isModalOpen("logoutAlert")]);

    const handleRefreshToken = () => {
        console.log("handleRefreshToken called, revokeTokenHandler:", revokeTokenHandler);
        if (revokeTokenHandler) {
            revokeTokenHandler();
        } else {
            console.error("❌ revokeTokenHandler is not available");
        }
    };

    return (
        <CustomModal
            modalName="logoutAlert"
            animation="fade-scale"
            modalSize="modal-sm"
            showCloseButton={false}
        >
            <Box className="modal-header">
                <Typography variant="h6" id="customModalLabel" sx={{ fontWeight: 600 }}>
                    Session Expired
                </Typography>
            </Box>
            <Box className="modal-body">
                <Alert severity="warning" sx={{ mb: 2 }}>
                    Your session has expired. Do you want to persist it?
                </Alert>
            </Box>
            <Box
                className="modal-footer"
                sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}
            >
                <Button text="No" onClick={logout} className="btn btn-secondary" />
                <Button text="Yes" onClick={handleRefreshToken} className="btn btn-primary" />
            </Box>
        </CustomModal>
    );
};

/**
 * Main Application Component
 * Handles authentication, routing, and global state
 */
const MainApp = () => {
    const [isLoading, setIsLoading] = useState(true);
    const { auth } = useAuthHandler(getStoredUserAuth());
    const { browserTitle } = useContext(BrowserTitleContext);
    const { globalLoading } = useContext(AuthContext);

    const { logout } = useLogoutHandler();
    const { redirectToAdmin, redirectToBase } = useRedirectHandler();

    const getuserinfo = async () => {
        try {
            if (auth && auth.authenticated) {
                info("User authenticated, redirecting to admin panel", { userId: auth.userId });
                redirectToAdmin();
            } else {
                info("User not signed in, redirecting to base");
                redirectToBase();
            }
            setIsLoading(false);
        } catch (err) {
            info("Error in root page initialization", { error: err.message });
            setIsLoading(false);
        }
    };

    useEffect(() => {
        getuserinfo();
        document.title = browserTitle;
    }, [browserTitle]);

    return (
        <Fragment>
            {isLoading ? (
                <Spinner text="Initializing application..." />
            ) : (
                <Fragment>
                    {globalLoading && (
                        <Box
                            sx={{
                                position: "fixed",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: "rgba(255, 255, 255, 0.95)",
                                zIndex: 10000,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 2,
                            }}
                        >
                            <Spinner text="Logging out, please wait..." />
                        </Box>
                    )}
                    <ErrorBoundary
                        title="Application Error"
                        message="We're sorry, but something went wrong. Please try refreshing the page."
                    >
                        <ErrorContextProvider>
                            <BrowserTitleContextProvider>
                                <WsSocketContextProvider>
                                    <AppRouter />
                                </WsSocketContextProvider>
                            </BrowserTitleContextProvider>
                        </ErrorContextProvider>
                    </ErrorBoundary>
                </Fragment>
            )}

            {/* Session Expiry Modal - Triggered by Apollo Client */}
            <SessionExpiryModal />

            <CustomModal
                modalName="unauthorizedAlert"
                animation="fade-scale"
                modalSize="modal-sm"
                showCloseButton={false}
            >
                <Box className="modal-header">
                    <Typography variant="h6" id="customModalLabel" sx={{ fontWeight: 600 }}>
                        Unauthorized Alert
                    </Typography>
                </Box>
                <Box className="modal-body">
                    <Alert severity="error" sx={{ mb: 2 }}>
                        <AlertTitle sx={{ fontWeight: 600 }}>Access Denied</AlertTitle>
                        You are not authorized to access this application. Please contact support if
                        you believe this is an error.
                    </Alert>
                </Box>
                <Box className="modal-footer" sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <Button text="Ok" type="button" className="btn btn-primary" onClick={logout} />
                </Box>
            </CustomModal>
        </Fragment>
    );
};

const ClearCacheComponent = withClearCache(MainApp);

/**
 * Root Application Component with Providers
 * Wraps the app with Material UI theme, error boundary, and context providers
 */
const App = () => {
    return (
        <ErrorBoundary
            title="Critical Application Error"
            message="We've encountered a critical error. The application will need to reload."
        >
            <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
                <ThemeProvider theme={theme}>
                    <CssBaseline />
                    <SnackbarProvider
                        maxSnack={3}
                        anchorOrigin={{
                            vertical: "top",
                            horizontal: "right",
                        }}
                        autoHideDuration={5000}
                    >
                        <AuthContextProvider>
                            <ClearCacheComponent />
                        </AuthContextProvider>
                    </SnackbarProvider>
                </ThemeProvider>
            </GoogleOAuthProvider>
        </ErrorBoundary>
    );
};

export default App;
